import React, { PropTypes } from 'react';
import cn from 'classnames';
import moment from 'moment';
import { holdItemShape, branchShape } from '@bibliocommons/bc-prop-types';
import Immutable from 'immutable';
import BcReactComponent from '@bibliocommons/base-bc-react-component';
import ImmutablePropTypes from 'react-immutable-proptypes';
import HoldsAdditionalBriefInfo from '../HoldsAdditionalBriefInfo';
import HoldsSecondaryInfo from '../HoldsSecondaryInfo/HoldsSecondaryInfo';
import CancelHoldButton from '../CancelHoldButton';
import PauseHoldButton from '../PauseHoldButton';
import EditHoldDatesButton from '../EditHoldDatesButton';
import ResumeHoldButton from '../ResumeHoldButton';
import AvailableForCheckoutButton from '../AvailableForCheckoutButton';
import AddToShelfButtonContainer from '../../../../containers/AddToShelfButtonContainer';
import ShortFormattedDate from '../../../shared/ShortFormattedDate';
import BorrowingBibBrief from '../../shared/BorrowingBibBrief';

import { FormattedMessage } from 'react-intl';

import BibListItem from '../../../shared/BibListItem';
import { WORKFLOW_VIEWS } from '../../../../constants/HoldsConstants';
import { ITEM_TRANSACTION_WORKFLOW } from '../../../../constants/WorkflowConstants';

import './HoldItem.scss';

export default class HoldItem extends BcReactComponent {
  constructor(props) {
    super(props);
    this.bindAll(
      'renderBibActions',
      'renderSecondaryInfo',
      'renderBibBrief',
      'handlePauseHolds',
      'handleResumeHolds',
      'handleCancelHolds',
      'handleAvailableCheckout',
      'handleUpdatePickupBranch',
      'handleUpdatePauseEndDate',
      'handleUpdateExpiryDate',
      'shouldShowEditDatesButton'
    );
  }

  handleAvailableCheckout() {
    const { item, workflowActions } = this.props;

    workflowActions.updateWorkflow(
      item.get('holdsId'),
      ITEM_TRANSACTION_WORKFLOW,
      Immutable.Map({
        metadataId: item.get('metadataId'),
        step: WORKFLOW_VIEWS.DIGITAL_CHECKOUT
      }),
      true,
      true
    );
  }

  handleCancelHolds() {
    const { item, workflowActions } = this.props;
    workflowActions.updateWorkflow(
      item.get('holdsId'),
      ITEM_TRANSACTION_WORKFLOW,
      Immutable.Map({
        metadataId: item.get('metadataId'),
        step: WORKFLOW_VIEWS.CANCEL_HOLD,
        holdsId: item.get('holdsId')
      }),
      true,
      true
    );
  }

  handlePauseHolds() {
    const { item, workflowActions, accountId, holdActions } = this.props;

    const enableStartDate = item.getIn(['bib', 'policy', 'materialPolicy', 'supportsSuspendHoldStartDateEdit']);
    const enableEndDate = item.getIn(['bib', 'policy', 'materialPolicy', 'supportsSuspendHoldEndDateEdit']);

    if (enableStartDate || enableEndDate) {
      workflowActions.updateWorkflow(
        item.get('holdsId'),
        ITEM_TRANSACTION_WORKFLOW,
        Immutable.Map({
          metadataId: item.get('metadataId'),
          step: WORKFLOW_VIEWS.PAUSE_HOLD
        }),
        true,
        true
      );
    } else {
      holdActions.pauseOrResumeHolds({
        accountId,
        holdIds: [item.get('holdsId')],
        workflowId: item.get('holdsId'),
        suspended:{
          startDate: null,
          endDate: moment().add(1, 'day').format(),
          status: true
        }
      });
    }

    return null;
  }

  handleResumeHolds() {
    const { item, accountId, holdActions } = this.props;
    holdActions.pauseOrResumeHolds({
      accountId,
      holdIds: [item.get('holdsId')],
      workflowId: item.get('holdsId'),
      suspended: { status: false }
    });
  }

  handleUpdatePickupBranch(location) {
    const { item, accountId, branches, holdActions } = this.props;

    const selectedBranch = branches
      .filter(branch => branch.get('code') === location)
      .first();

    holdActions.savePickupLocation({
      accountId,
      holdId: item.get('holdsId'),
      branch: selectedBranch
    });
  }

  handleUpdatePauseEndDate(endDate) {
    const { item, handleUpdateHoldsPauseEndDate } = this.props;
    handleUpdateHoldsPauseEndDate([item.get('holdsId')], endDate);
  }

  handleUpdateExpiryDate(expiryDate) {
    const { item, handleUpdateHoldsExpiryDate } = this.props;
    handleUpdateHoldsExpiryDate([item.get('holdsId')], expiryDate);
  }

  isHoldPausable() {
    const { item } = this.props;
    return item.get('status') !== 'READY_FOR_PICKUP' && item.get('status') !== 'SUSPENDED'
      && item.get('actions', Immutable.List()).includes('suspend');
  }

  shouldShowEditDatesButton() {
    const { item } = this.props;
    return item.get('status') !== 'READY_FOR_PICKUP' && item.get('status') !== 'SUSPENDED'
      && item.get('actions', Immutable.List()).includes('updateSuspend');
  }

  canResumeHolds() {
    const { item } = this.props;
    return item.get('status') === 'SUSPENDED'
      && item.get('actions', Immutable.List()).includes('activate');
  }

  isAvailableForCheckout() {
    return this.props.item.get('materialType') === 'DIGITAL' &&
      this.props.item.get('actions', Immutable.List()).includes('checkout');
  }

  renderPauseResumeActions() {
    const { holdsTransactions, item, actionInProgress } = this.props;
    const isPausing = holdsTransactions.getIn([item.get('holdsId'), 'isPausing'], false);
    const isResuming = holdsTransactions.getIn([item.get('holdsId'), 'isResuming'], false);

    if (this.shouldShowEditDatesButton()) {
      return (
        <EditHoldDatesButton
          disabled={actionInProgress && !isPausing}
          handleClick={this.handlePauseHolds}
          isLoading={isPausing} />
      );
    } else if (this.isHoldPausable()) {
      return (
        <PauseHoldButton
          disabled={actionInProgress && !isPausing}
          handleClick={this.handlePauseHolds}
          isLoading={isPausing} />
      );
    } else if (this.canResumeHolds()) {
      return (
        <ResumeHoldButton
          disabled={actionInProgress && !isResuming}
          handleClick={this.handleResumeHolds}
          isLoading={isResuming} />
      );
    }

    return null;
  }

  renderCannotModifyMessage() {
    const isReadyForPickup = this.props.item.get('status') === 'READY_FOR_PICKUP';
    const isInTransit = this.props.item.get('status') === 'IN_TRANSIT';

    return (
      <div className="cannot-modify-message">
        {isReadyForPickup && !this.isAvailableForCheckout() && <FormattedMessage id="holds_cannot_pause_ready_for_pickup" />}
        {isInTransit && <FormattedMessage id="holds_in_transit_cannot_be_modified" />}
      </div>
    );
  }

  renderBibActions() {
    const { item, holdsTransactions, shelfTransactions, actionInProgress } = this.props;
    const isCancelling = holdsTransactions.getIn([item.get('holdsId'), 'isCancelling'], false);
    const shelfActionInProgress = !shelfTransactions.get(item.get('metadataId'), Immutable.Map()).isEmpty();
    const canCancel = item.get('actions', Immutable.List()).includes('cancel');

    return (
      <div className="actions">
        { this.isAvailableForCheckout() &&
          <AvailableForCheckoutButton disabled={actionInProgress} handleClick={this.handleAvailableCheckout} />
        }
        {this.renderPauseResumeActions()}
        {canCancel && <CancelHoldButton
          disabled={actionInProgress && !isCancelling}
          isLoading={isCancelling}
          handleClick={this.handleCancelHolds} />}
        {this.renderCannotModifyMessage()}
        { this.props.item.get('bib') &&
          <div className="holds-shelf-button">
            <AddToShelfButtonContainer
              disabled={actionInProgress && !shelfActionInProgress}
              metadataId={this.props.item.get('metadataId')}
              workflowId={this.props.item.get('holdsId')} />
          </div>
        }
      </div>
    );
  }

  renderDatePlaced() {
    const { item } = this.props;
    const status = item.get('status');
    const bibType = item.get('materialType');
    const datePlaced = item.get('holdPlacedDate');

    return (['NOT_YET_AVAILABLE', 'SUSPENDED'].includes(status) && bibType === 'PHYSICAL' && datePlaced && (
      <div className="holds-placed-date">
        <FormattedMessage
          key="holds-placed-date"
          id="placed_on_date"
          values={{ date: <ShortFormattedDate value={item.get('holdPlacedDate')} /> }} />
      </div>
    ));
  }

  renderSecondaryInfo() {
    const { item, branches, holdsTransactions, actionInProgress } = this.props;
    return (
      <div>
        <div className={cn('holds-status', item.get('status').toLowerCase())}>
          <HoldsSecondaryInfo
            actionInProgress={actionInProgress}
            item={item}
            branches={branches}
            handleUpdatePickupBranch={this.handleUpdatePickupBranch}
            handleUpdatePauseEndDate={this.handleUpdatePauseEndDate}
            handleUpdateExpiryDate={this.handleUpdateExpiryDate}
            holdsTransactions={holdsTransactions} />
        </div>
        {this.renderBibActions()}
        {this.renderDatePlaced()}
      </div>
    );
  }

  renderBibBrief() {
    const { item, availabilityDetailActions } = this.props;
    const briefInfo = item.getIn(['bib', 'briefInfo']);

    return (
      <BorrowingBibBrief bib={briefInfo} fallbackTitle={this.props.item.get('bibTitle')}>
        <HoldsAdditionalBriefInfo item={item} availabilityDetailActions={availabilityDetailActions} />
      </BorrowingBibBrief>
    );
  }

  render() {
    const { item } = this.props;

    return (
      <BibListItem
        workflowId={item.get('holdsId')}
        className={cn('cp-hold-item', item.get('status').toLowerCase())}
        bib={item.getIn(['bib', 'briefInfo'], Immutable.Map())}
        renderInfo={() => this.renderSecondaryInfo()}
        renderBibBrief={() => this.renderBibBrief()} />
    );
  }
}

HoldItem.propTypes = {
  actionInProgress: PropTypes.bool,
  accountId: PropTypes.number,
  item: holdItemShape,
  branches: ImmutablePropTypes.listOf(branchShape).isRequired,
  holdsTransactions: ImmutablePropTypes.map,
  shelfTransactions: ImmutablePropTypes.map,
  workflowActions: PropTypes.objectOf(PropTypes.func),
  holdActions: PropTypes.objectOf(PropTypes.func),
  availabilityDetailActions: PropTypes.objectOf(PropTypes.func),
  handleUpdateHoldsPauseEndDate: PropTypes.func,
  handleUpdateHoldsExpiryDate: PropTypes.func
};



// WEBPACK FOOTER //
// ./app/components/borrowing/holds/HoldItem/HoldItem.jsx